import { useState } from "react";
import "./Marketplace.scss";
import MarketplaceHeader from "./MarketplaceHeader";
import MarketplaceTab from "./MarketplaceTab";
import MarketplaceCard from "./MarketplaceCard";
import axios from "axios";
import FailModal from "../../../../../../../../../.././.././../../../Components/FailModal";
import { useADSContext } from "../../../../../../../../../../context/ADSProvider";

const TabItems = [
  {
    id: "shopsNearby",
    name: "Shops Nearby",
  },
  {
    id: "favorites",
    name: "Favorites",
  },
];

const drawerAnimationDuration = 300;

const Marketplace = ({ disabled }) => {
  const { setGlobalLoading, selectedProduct } = useADSContext();
  const [selectedTab, setSelectedTab] = useState(TabItems[0]);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(5);
  const [lat, setLat] = useState(null);
  const [long, setLong] = useState(null);
  const [nearbyStores, setNearbyStores] = useState([]);
  const [totalNearbyStores, setTotalNearbyStores] = useState(0);
  const [loading, setLoading] = useState(false);

  // separate showDrawer state & openDrawer state for the sake of animation only
  const [showDrawer, setShowDrawer] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);

  const [errorModal, setErrorModal] = useState({
    show: false,
    title: "",
    description: "",
    retry: false,
  });

  const isFavorites = selectedTab.id === "favorites";
  const productId = selectedProduct?.id || 0;

  const resetFailModal = () => {
    setErrorModal({
      show: false,
      title: "",
      description: "",
      retry: false,
    });
  };

  const getLocationByGoogle = async () => {
    return new Promise((resolve) => {
      axios
        .post(
          `https://www.googleapis.com/geolocation/v1/geolocate?key=AIzaSyCCGButEj3WDKTHDcUz6B1M5ukr_nw6lv4`
        )
        .then((res) => resolve([null, res.data]))
        .catch((err) => resolve([err, null]));
    });
  };

  const getLocationByBrowser = () => {
    return new Promise((resolve) => {
      const handleSuccess = (data) => resolve([null, data]);
      const handleError = (err) => resolve([err, null]);

      navigator.geolocation.getCurrentPosition(handleSuccess, handleError);
    });
  };

  const handleTabSelect = async (nextSelectedTab) => {
    if (nextSelectedTab.id !== selectedTab.id) {
      setLoading(true);
      const resetPage = 0;
      setSelectedTab(nextSelectedTab);
      setPage(resetPage);
      setNearbyStores([]);
      setTotalNearbyStores(0);

      const favorite = nextSelectedTab.id === "favorites";
      const [_errNearbyStore, nearbyStores] = await getNearbyStores({
        lat,
        long,
        page: resetPage,
        pageSize,
        favorite,
      });

      setNearbyStores(nearbyStores);
      setLoading(false);
    }
  };

  const getNearbyStores = ({ lat, long, page, pageSize, favorite = false }) => {
    return new Promise((resolve) => {
      let Req = {
        method: "GET",
        headers: {
          Authorization: "Bearer " + window.localStorage.getItem("Token"),
        },
        baseURL:
          window.localStorage.getItem("BaseURL") +
          `/services/projectmanagement/api/products/${productId}/shops?page=${page}&size=${pageSize}&sort=distance,asc&latlng=${lat},${long}${
            favorite ? `&favorite=true` : ""
          }`,
      };

      axios(Req)
        .then((res) => {
          setTotalNearbyStores(res.headers["x-total-count"]);
          resolve([null, res.data]);
        })
        .catch((err) => resolve([err, null]));
    });
  };

  const handleOpenDrawer = async () => {
    if (disabled) {
      return;
    }

    setGlobalLoading(true);
    let currentLat = null,
      currentLong = null;

    const [errLocByGoogle, locByGoogle] = await getLocationByGoogle();

    if (locByGoogle) {
      currentLat = locByGoogle.location.lat;
      currentLong = locByGoogle.location.lng;
    }

    // handle in case google api doesn't work, check using browser's geolocation api
    if (errLocByGoogle) {
      const [errLocByBrowser, locByBrowser] = await getLocationByBrowser();

      if (locByBrowser) {
        currentLat = locByBrowser.coords.latitude;
        currentLong = locByBrowser.coords.longitude;
      }

      if (errLocByBrowser) {
        setErrorModal({
          show: true,
          title: "Location Not Available",
          description:
            "We couldn't determine your location, which is needed to find stores nearby.",
          retry: false,
        });
        return;
      }
    }

    const [errNearbyStore, nearbyStores] = await getNearbyStores({
      lat: currentLat,
      long: currentLong,
      page,
      pageSize,
    });
    if (errNearbyStore) {
      if (
        errNearbyStore.response.data.detail.includes(
          "Not a wedding-related image"
        )
      ) {
        setErrorModal({
          show: true,
          title: " Image Not Recognized as Wedding-Related",
          description:
            "The uploaded or generated image does not appear to be related to a wedding product (e.g., dress, suit, ring, cake, flowers, decorations).",
          retry: false,
        });
      } else {
        setErrorModal({
          show: true,
          title: "Something Went Wrong",
          description:
            "An unexpected error occurred while processing your request.",
          retry: true,
        });
      }
      setGlobalLoading(false);
      return;
    }

    setNearbyStores(nearbyStores);
    setLat(currentLat);
    setLong(currentLong);
    setOpenDrawer(true);
    setShowDrawer(true);
    setGlobalLoading(false);
  };

  const handleCloseDrawer = () => {
    resetFailModal();
    setLat(null);
    setLong(null);
    setOpenDrawer(false);
    setTimeout(() => {
      setShowDrawer(false);
    }, drawerAnimationDuration);
  };

  const handleShowMore = async () => {
    setLoading(true);
    const nextPage = page + 1;

    const [errNearbyStore, nearbyStores] = await getNearbyStores({
      lat,
      long,
      page: nextPage,
      pageSize,
      favorite: isFavorites,
    });
    if (errNearbyStore) {
      if (errNearbyStore.response.data.detail.includes("not wedding-related")) {
        setErrorModal({
          show: true,
          title: "Image Not Recognized as Wedding-Related",
          description:
            "The uploaded image does not appear to be related to a wedding product (e.g., dress, suit, ring, cake, flowers, decorations).",
          retry: true,
        });
      } else {
        setErrorModal({
          show: true,
          title: "Something Went Wrong",
          description:
            "An unexpected error occurred while processing your request.",
          retry: true,
        });
      }
      setLoading(false);
      return;
    }

    setNearbyStores((prev) => [...prev, ...nearbyStores]);
    setPage(nextPage);
    setLoading(false);
  };

  const handleRemoveFavorite = (removedStore) => {
    // only remove in favorite tab
    if (isFavorites) {
      setNearbyStores((prevStores) =>
        prevStores.filter((prevStore) => prevStore.id !== removedStore.id)
      );
      setTotalNearbyStores((prev) => prev - 1);
    }
  };

  return (
    <>
      <div
        className={`marketplace__trigger ${disabled ? "disabled" : ""}`}
        onClick={handleOpenDrawer}
      >
        Shops Nearby
      </div>

      {showDrawer && (
        <>
          <div className="marketplace" onClick={handleCloseDrawer} />
          <div
            className={`marketplace__container ${
              openDrawer ? "animate-slide-in" : "animate-slide-out"
            }`}
          >
            {/*  Header*/}
            <MarketplaceHeader onClose={handleCloseDrawer} />

            {/*  Tab*/}
            <MarketplaceTab
              tabItems={TabItems}
              selectedTab={selectedTab}
              onTabSelect={handleTabSelect}
            />

            {/*  Cards*/}
            <div className="marketplace__container--card-container">
              {nearbyStores.map((item) => (
                <MarketplaceCard
                  key={item.id}
                  item={item}
                  onRemoveFavorite={handleRemoveFavorite}
                />
              ))}

              {nearbyStores.length < totalNearbyStores && !loading && (
                <span className="show-more" onClick={handleShowMore}>
                  More..
                </span>
              )}
              {loading && <span className="loading">Loading...</span>}
              {!loading && nearbyStores.length <= 0 && (
                <span className="loading">
                  {isFavorites ? "No favorites store" : "No stores nearby"}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      <FailModal
        {...errorModal}
        onCancel={resetFailModal}
        onRetry={errorModal.retry && handleOpenDrawer}
        cancelText="Close"
      />
    </>
  );
};

export default Marketplace;
